<script setup lang="ts">
import type { Question } from '../../../question/models/Question'

const props = defineProps<{
  question: Pick<Question<'maxDiffSet'>, 'bestLabel' | 'worstLabel' | 'fields'>
  bestFieldId: string | undefined
  worstFieldId: string | undefined
}>()

const emit = defineEmits<{
  selectField: [fieldId: string, type: 'best' | 'worst']
}>()
</script>
<template>
  <div class="max-diff-set-question">
    <div class="max-diff-set-question__labels">
      <div class="max-diff-set-question__label">
        {{ props.question.bestLabel }}
      </div>
      <div class="max-diff-set-question__label">
        {{ props.question.worstLabel }}
      </div>
    </div>
    <div class="max-diff-set-question__fields">
      <div
        v-for="field in props.question.fields.filter(f => f.type === 'maxDiffSetChoice')"
        :key="field.id"
        class="max-diff-set-question__field"
      >
        <input
          name="best"
          :checked="field.id === props.bestFieldId"
          :disabled="field.id === props.worstFieldId"
          class="max-diff-set-question__button"
          type="radio"
          :onClick="() => emit('selectField', field.id, 'best')"
          :data-test-id="`MaxDiffSetQuestion_Best_Field-${field.id}`"
        />
        <div class="max-diff-set-question__field-title">
          {{ field.title }}
        </div>
        <input
          name="worst"
          :checked="field.id === props.worstFieldId"
          :disabled="field.id === props.bestFieldId"
          class="max-diff-set-question__button"
          type="radio"
          :onClick="() => emit('selectField', field.id, 'worst')"
          :data-test-id="`MaxDiffSetQuestion_Worst_Field-${field.id}`"
        />
      </div>
    </div>
  </div>
</template>
<style lang="postcss">
.max-diff-set-question {
  display: grid;
  padding: var(--attest-spacing-4);
  gap: var(--attest-spacing-4);
  height: 100%;
  overflow: auto;
  grid-template-rows: min-content auto;

  &__labels {
    --gap: var(--attest-spacing-2);

    display: grid;
    grid-template-columns: repeat(2, calc(50% - var(--gap) * 0.5));
    gap: var(--gap);
    justify-content: space-between;
  }

  &__label {
    font-size: 10px;
    color: #036;
    font-weight: 600;
    text-transform: uppercase;

    &:last-of-type {
      text-align: right;
    }
  }

  --border-color: #c0c2c2;
  &__field {
    display: grid;
    background-color: #fff;
    grid-template-columns: 44px auto 44px;
    text-align: center;
    align-items: center;
  }

  &__field:last-of-type {
    border-bottom: 1px solid var(--border-color);
  }
  &__field-title {
    padding: var(--attest-spacing-3) var(--attest-spacing-2);
    border-top: 1px solid var(--border-color);
  }

  &__button {
    position: relative;
    display: grid;
    place-items: center;
    appearance: none;
    margin: 0;
    padding: var(--attest-spacing-2);
    height: 100%;
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
    border-top: 1px solid var(--border-color);

    &:disabled {
      background-color: #f5f5f5;
    }

    &:not(:disabled):not(:checked):hover::before {
      border-color: #6e6f6f;
    }

    &::before {
      display: block;
      content: '';
      width: 16px;
      height: 16px;
      border: 2px solid var(--border-color);
      border-radius: 50%;
    }

    &:checked::before {
      border-color: #00829b;
    }

    &:checked::after {
      position: absolute;
      content: '';
      width: 10px;
      height: 10px;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background-color: #00829b;
    }
  }
}
</style>
